import { Injectable } from '@angular/core';
import {
  AppEditionsMarketing,
  CreateEditionRequest,
  DeleteEditionRequest,
  Edition,
  EditionBillingFrequency,
  EditionMarketingInformationInterface,
  EditionsApiService,
  GetEditionRequest,
  GetMultiDraftEditionRequest,
  GetMultiEditionForMultiAppRequest,
  GetMultiEditionRequest,
  ListDraftEditionsForAppRequest,
  ListEditionsForAppRequest,
  ListEditionsMarketingForAppRequest,
  Release,
  UpdateMultiEditionRequest,
  UpsertEditionsMarketingRequest,
} from '@vendasta/marketplace-apps/v1';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface EditionIdentifiers {
  appId: string;
  editionId: string;
}

export interface EditionPricingData {
  wholesalePrice: number;
  suggestedRetailPrice: number;
  billingFrequency: EditionBillingFrequency;
}

export interface EditionData {
  editionId: string;
  name: string;
  pricing: EditionPricingData;
}

@Injectable({ providedIn: 'root' })
export class EditionsService {
  constructor(private apiService: EditionsApiService) {}

  get(appId: string, editionId: string): Observable<Edition> {
    const req = new GetEditionRequest({
      appId: appId,
      editionId: editionId,
    });
    return this.apiService.get(req).pipe(map((resp) => resp.edition));
  }

  getMulti(appId: string, editionIds: string[]): Observable<Edition[]> {
    const req = new GetMultiEditionRequest({
      appId: appId,
      editionIds: editionIds,
    });
    return this.apiService.getMulti(req).pipe(map((resp) => resp.editions));
  }

  getMultiDraft(appId: string, editionIds: string[]): Observable<Edition[]> {
    const req = new GetMultiDraftEditionRequest({
      appId: appId,
      editionIds: editionIds,
    });
    return this.apiService.getMultiDraft(req).pipe(map((resp) => resp.editions));
  }

  listEditionsForApp(appId: string, cursor: string, pageSize: number): Observable<Edition[]> {
    const req = new ListEditionsForAppRequest({
      appId: appId,
      cursor: cursor,
      pageSize: pageSize,
    });
    return this.apiService.listEditionsForApp(req).pipe(map((resp) => resp.editions));
  }

  listDraftEditionsForApp(appId: string, cursor: string, pageSize: number): Observable<Edition[]> {
    const req = new ListDraftEditionsForAppRequest({
      appId: appId,
      cursor: cursor,
      pageSize: pageSize,
    });
    return this.apiService.listDraftEditionsForApp(req).pipe(map((resp) => resp.editions));
  }

  getMultiEditionForMultiApp(editionIdentifiersList: EditionIdentifiers[]): Observable<Edition[]> {
    const req = new GetMultiEditionForMultiAppRequest({
      appEditionIdPairs: editionIdentifiersList,
    });
    return this.apiService.getMultiEditionForMultiApp(req).pipe(map((resp) => resp.editions));
  }

  updateMulti(appId: string, editionData: EditionData[]): Observable<boolean> {
    const req = new UpdateMultiEditionRequest({
      appId: appId,
      editions: editionData,
    });
    return this.apiService.updateMulti(req).pipe(map(() => true));
  }

  create(appId: string, editionName: string): Observable<Edition> {
    const req = new CreateEditionRequest({
      appId,
      name: editionName,
    });
    return this.apiService.create(req).pipe(map((resp) => resp.edition));
  }

  deleteEdition(appId: string, editionId: string): Observable<boolean> {
    const req = new DeleteEditionRequest({
      appId: appId,
      editionId: editionId,
    });
    return this.apiService.delete(req).pipe(map(() => true));
  }

  upsertEditionsMarketing(
    appId: string,
    editionId: string,
    endUserMarketing: EditionMarketingInformationInterface,
    usesResellerMarketing: boolean,
    resellerMarketing: EditionMarketingInformationInterface,
  ): Observable<boolean> {
    const req = new UpsertEditionsMarketingRequest({
      appId: appId,
      editionId: editionId,
      endUserMarketing: endUserMarketing,
      usesResellerMarketing: usesResellerMarketing,
      resellerMarketing: resellerMarketing,
    });
    return this.apiService.upsertEditionsMarketing(req).pipe(map(() => true));
  }

  listEditionsMarketingForApp(appId: string, release: Release): Observable<AppEditionsMarketing[]> {
    const req = new ListEditionsMarketingForAppRequest({
      appId: appId,
      releaseType: release,
    });
    return this.apiService.listEditionsMarketingForApp(req).pipe(
      map((aem) => {
        return aem.appEditionsMarketing;
      }),
    );
  }
}
